export type ThemeId = 'amor' | 'carrera' | 'proposito' | 'familia' | 'bienestar'

export interface Theme {
  id: ThemeId
  title: string
  subtitle: string
  icon: string
  description: string
}

export interface Question {
  id: string
  text: string
  justification: string
  options: { label: string; value: string }[]
}

export const themes: Theme[] = [
  {
    id: 'amor',
    title: 'VÍNCULOS Y RELACIONES',
    subtitle:
      'Análisis de compatibilidad, futuro de pareja y bloqueos emocionales.',
    icon: 'Heart',
    description:
      'Análisis de compatibilidad, futuro de pareja y bloqueos emocionales.',
  },
  {
    id: 'carrera',
    title: 'PROYECCIÓN PROFESIONAL',
    subtitle:
      'Claridad sobre finanzas, toma de decisiones laborales y crecimiento económico.',
    icon: 'Coins',
    description:
      'Claridad sobre finanzas, toma de decisiones laborales y crecimiento económico.',
  },
  {
    id: 'proposito',
    title: 'LÍNEA DE DESTINO',
    subtitle:
      'Propósito de vida, toma de decisiones importantes y claridad de camino.',
    icon: 'Compass',
    description:
      'Propósito de vida, toma de decisiones importantes y claridad de camino.',
  },
  {
    id: 'familia',
    title: 'NÚCLEO Y LINAJE',
    subtitle:
      'Sanación de vínculos familiares y protección del entorno cercano.',
    icon: 'Home',
    description:
      'Sanación de vínculos familiares y protección del entorno cercano.',
  },
  {
    id: 'bienestar',
    title: 'EQUILIBRIO VITAL',
    subtitle:
      'Identificación de fugas energéticas y restauración del bienestar personal.',
    icon: 'Sparkles',
    description:
      'Identificación de fugas energéticas y restauración del bienestar personal.',
  },
]

export const questionsByTheme: Record<ThemeId, Question[]> = {
  amor: [
    {
      id: 'amor_1',
      text: '¿En qué momento del día el peso de la soledad o la desconexión se vuelve más insoportable?',
      justification:
        'El Oráculo necesita medir la resonancia de tu vacío emocional para identificar el tipo de lazo kármico que te falta.',
      options: [
        { label: 'Al despertar, cuando la realidad golpea.', value: 'manana' },
        {
          label: 'En el silencio de la noche, antes de dormir.',
          value: 'noche',
        },
        {
          label: 'En medio de multitudes, sintiéndome invisible.',
          value: 'multitud',
        },
        {
          label: 'Rara vez lo siento, pero cuando llega, me paraliza.',
          value: 'esporadico',
        },
      ],
    },
    {
      id: 'amor_2',
      text: 'Cuando imaginas a tu alma gemela, ¿qué es lo primero que tu intuición percibe?',
      justification:
        'Las tablillas antiguas revelan que la primera imagen mental es un eco de una vida pasada compartida.',
      options: [
        {
          label: 'Una mirada profunda que me comprende sin palabras.',
          value: 'mirada',
        },
        { label: 'Una sensación de paz y refugio absoluto.', value: 'paz' },
        {
          label: 'Una energía magnética y apasionada que me consume.',
          value: 'pasion',
        },
        {
          label: 'Un compañero de batallas, alguien que camina a mi lado.',
          value: 'companero',
        },
      ],
    },
    {
      id: 'amor_3',
      text: '¿Cuál es el patrón oculto que parece repetirse en tus decepciones amorosas?',
      justification:
        'Reconocer la herida repetitiva permite a la Llave de Constantinopla girar en la cerradura correcta de tu destino.',
      options: [
        {
          label: 'Doy demasiado y recibo migajas a cambio.',
          value: 'dar_demasiado',
        },
        {
          label: 'Atraigo a personas emocionalmente indisponibles.',
          value: 'indisponibles',
        },
        {
          label: 'El fuego inicial se apaga rápidamente sin explicación.',
          value: 'fuego_apaga',
        },
        {
          label: 'El miedo al abandono me hace sabotear la relación.',
          value: 'sabotaje',
        },
      ],
    },
    {
      id: 'amor_4',
      text: 'Si tu corazón fuera un templo antiguo, ¿en qué estado se encontraría hoy?',
      justification:
        'El estado de tu templo interno dicta qué tipo de energía amorosa eres capaz de albergar actualmente.',
      options: [
        {
          label: 'En ruinas, pero con los cimientos aún fuertes.',
          value: 'ruinas',
        },
        {
          label: 'Cerrado con llave, custodiado por el miedo.',
          value: 'cerrado',
        },
        {
          label: 'Abierto, pero lleno de polvo y ecos del pasado.',
          value: 'polvo',
        },
        {
          label: 'Restaurándose, preparándose para un nuevo culto.',
          value: 'restaurando',
        },
      ],
    },
    {
      id: 'amor_5',
      text: '¿Qué sacrificio estarías dispuesto a hacer para encontrar el amor verdadero dictado por las estrellas?',
      justification:
        'El universo exige un intercambio equivalente. Tu respuesta sella el pacto con el Oráculo.',
      options: [
        { label: 'Soltar mi necesidad de control absoluto.', value: 'control' },
        {
          label: 'Perdonar a quienes me rompieron en el pasado.',
          value: 'perdon',
        },
        {
          label: 'Derribar los muros que construí para protegerme.',
          value: 'muros',
        },
        {
          label: 'Aceptar que el amor verdadero requiere vulnerabilidad total.',
          value: 'vulnerabilidad',
        },
      ],
    },
  ],
  carrera: [
    {
      id: 'car_1',
      text: 'Cuando piensas en tu situación financiera actual, ¿qué emoción primaria domina tu pecho?',
      justification:
        'El dinero es energía. El Oráculo debe leer la vibración de tu bloqueo financiero.',
      options: [
        { label: 'Una ansiedad asfixiante por el futuro.', value: 'ansiedad' },
        {
          label: 'Frustración, siento que mi esfuerzo no es recompensado.',
          value: 'frustracion',
        },
        {
          label: 'Apatía, he perdido la pasión por lo que hago.',
          value: 'apatia',
        },
        {
          label: 'Miedo a perder lo poco o mucho que he construido.',
          value: 'miedo',
        },
      ],
    },
    {
      id: 'car_2',
      text: '¿Cuál crees que es tu talento más subestimado, aquel que el mundo aún no ha sabido valorar?',
      justification:
        'La Llave de Constantinopla busca el oro oculto en tu carta natal para alinearlo con tu prosperidad.',
      options: [
        {
          label: 'Mi capacidad para ver soluciones donde otros ven caos.',
          value: 'soluciones',
        },
        {
          label: 'Mi empatía y habilidad para conectar con los demás.',
          value: 'empatia',
        },
        {
          label: 'Mi creatividad inagotable y visión original.',
          value: 'creatividad',
        },
        {
          label: 'Mi resistencia y capacidad de trabajo incansable.',
          value: 'resistencia',
        },
      ],
    },
    {
      id: 'car_3',
      text: 'Si el dinero no existiera, ¿a qué dedicarías las horas de tu vida?',
      justification:
        'Despojar a la mente de la ilusión material revela el verdadero propósito que atrae la abundancia natural.',
      options: [
        {
          label: 'A crear arte, escribir o expresarme libremente.',
          value: 'arte',
        },
        {
          label: 'A sanar, enseñar o guiar a otros en su camino.',
          value: 'sanar',
        },
        {
          label: 'A explorar el mundo, aprender y descubrir misterios.',
          value: 'explorar',
        },
        {
          label: 'A construir algo duradero para mi comunidad o familia.',
          value: 'construir',
        },
      ],
    },
    {
      id: 'car_4',
      text: '¿Qué creencia limitante sobre el éxito heredaste de tus ancestros?',
      justification:
        'Los votos de pobreza ancestrales son cadenas invisibles. Debemos identificarlas para romperlas.',
      options: [
        {
          label: '"El dinero corrompe el alma y trae problemas."',
          value: 'corrompe',
        },
        {
          label: '"Solo a través del sufrimiento extremo se logra algo."',
          value: 'sufrimiento',
        },
        {
          label: '"No nacimos para ser ricos, conformate con lo que hay."',
          value: 'conformismo',
        },
        { label: '"El éxito te aleja de tus seres queridos."', value: 'aleja' },
      ],
    },
    {
      id: 'car_5',
      text: '¿Estás listo para asumir el peso y la responsabilidad que conlleva la verdadera abundancia?',
      justification:
        'La prosperidad exige un recipiente fuerte. El Oráculo evalúa tu preparación espiritual.',
      options: [
        {
          label: 'Sí, estoy listo para liderar y expandir mi luz.',
          value: 'si_liderar',
        },
        {
          label: 'Tengo dudas, pero estoy dispuesto a aprender.',
          value: 'dudas',
        },
        {
          label: 'Me asusta, pero el deseo de crecer es mayor.',
          value: 'asusta',
        },
        { label: 'Solo quiero paz, no necesito un imperio.', value: 'paz' },
      ],
    },
  ],
  proposito: [
    {
      id: 'prop_1',
      text: '¿Qué sensación te invade cuando miras las estrellas en una noche despejada?',
      justification:
        'Tu conexión con el cosmos revela la antigüedad de tu alma y su misión original.',
      options: [
        {
          label: 'Nostalgia, como si extrañara un hogar lejano.',
          value: 'nostalgia',
        },
        {
          label: 'Insignificancia, pero de una manera pacífica.',
          value: 'insignificancia',
        },
        {
          label: 'Asombro y un profundo deseo de comprender los misterios.',
          value: 'asombro',
        },
        {
          label: 'Una certeza de que soy parte de un plan divino mayor.',
          value: 'certeza',
        },
      ],
    },
    {
      id: 'prop_2',
      text: '¿Qué tipo de dolor ajeno te resulta más difícil de presenciar sin intervenir?',
      justification:
        'Aquello que te duele del mundo es exactamente lo que viniste a sanar.',
      options: [
        {
          label: 'La injusticia y el abuso de poder sobre los vulnerables.',
          value: 'injusticia',
        },
        {
          label: 'La soledad y el abandono emocional de las personas.',
          value: 'soledad',
        },
        {
          label: 'La ignorancia que lleva a la destrucción de la naturaleza.',
          value: 'ignorancia',
        },
        {
          label: 'El potencial desperdiciado y los sueños rotos.',
          value: 'potencial',
        },
      ],
    },
    {
      id: 'prop_3',
      text: 'Si tu vida fuera un libro antiguo, ¿cuál sería el título del capítulo que estás viviendo ahora?',
      justification:
        'Nombrar tu presente permite al Oráculo ubicarte en la línea temporal de tu destino.',
      options: [
        { label: '"El Despertar en la Oscuridad"', value: 'despertar' },
        { label: '"La Travesía por el Desierto"', value: 'desierto' },
        { label: '"Reconstruyendo las Ruinas"', value: 'ruinas' },
        { label: '"En Busca de la Llave Perdida"', value: 'llave' },
      ],
    },
    {
      id: 'prop_4',
      text: '¿Qué es lo que más temes que digan de ti cuando ya no estés en este plano?',
      justification:
        'El miedo al legado revela la brújula moral de tu espíritu.',
      options: [
        {
          label: 'Que pasé por la vida sin dejar una huella real.',
          value: 'sin_huella',
        },
        {
          label: 'Que no tuve el valor de ser quien realmente era.',
          value: 'cobardia',
        },
        {
          label: 'Que lastimé a otros por mi propia confusión.',
          value: 'lastimar',
        },
        {
          label: 'Que me rendí antes de alcanzar mi verdadero potencial.',
          value: 'rendirse',
        },
      ],
    },
    {
      id: 'prop_5',
      text: '¿Estás dispuesto a dejar atrás la versión actual de ti mismo para abrazar tu destino?',
      justification:
        'La transformación requiere muerte simbólica. El Oráculo exige tu consentimiento.',
      options: [
        {
          label: 'Sí, que el fuego purifique lo que ya no me sirve.',
          value: 'fuego',
        },
        {
          label: 'Me aterra, pero sé que es el único camino.',
          value: 'aterra',
        },
        {
          label: 'Necesito tiempo, pero estoy dando los primeros pasos.',
          value: 'tiempo',
        },
        {
          label: 'Aún me aferro a mi vieja identidad, pero quiero soltarla.',
          value: 'aferro',
        },
      ],
    },
  ],
  familia: [
    {
      id: 'fam_1',
      text: '¿Qué emoción predomina cuando piensas en tu linaje materno o paterno?',
      justification:
        'Las raíces del árbol familiar dictan los frutos de tu presente. Debemos leer su savia.',
      options: [
        {
          label: 'Un peso de responsabilidades y expectativas no cumplidas.',
          value: 'peso',
        },
        {
          label: 'Un profundo amor mezclado con dolor incomprensible.',
          value: 'amor_dolor',
        },
        {
          label: 'Desconexión, siento que no pertenezco a ellos.',
          value: 'desconexion',
        },
        {
          label: 'Gratitud, aunque reconozco sus profundas heridas.',
          value: 'gratitud',
        },
      ],
    },
    {
      id: 'fam_2',
      text: '¿Cuál es el "secreto a voces" o el trauma no resuelto que ronda a tu familia?',
      justification:
        'Los secretos familiares son bloqueos kármicos. Nombrarlos es el primer paso para disolverlos.',
      options: [
        { label: 'Pérdidas financieras o ruina en el pasado.', value: 'ruina' },
        {
          label: 'Amores prohibidos, traiciones o abandonos.',
          value: 'abandonos',
        },
        {
          label: 'Enfermedades silenciadas o tragedias repentinas.',
          value: 'tragedias',
        },
        {
          label: 'Conflictos de poder y separaciones amargas.',
          value: 'conflictos',
        },
      ],
    },
    {
      id: 'fam_3',
      text: '¿En qué rol te has posicionado inconscientemente dentro de tu núcleo familiar?',
      justification:
        'El Oráculo identifica tu contrato álmico para saber si debes romperlo o honrarlo.',
      options: [
        {
          label: 'El sanador/salvador que carga con los problemas de todos.',
          value: 'salvador',
        },
        {
          label: 'La oveja negra que rompe las reglas y es incomprendida.',
          value: 'oveja_negra',
        },
        {
          label: 'El mediador invisible que busca la paz a costa de sí mismo.',
          value: 'mediador',
        },
        {
          label: 'El pilar fuerte que no tiene permiso para derrumbarse.',
          value: 'pilar',
        },
      ],
    },
    {
      id: 'fam_4',
      text: 'Si pudieras enviar un mensaje a tus ancestros, ¿qué les dirías?',
      justification:
        'Tu comunicación con el más allá revela tu nivel de madurez espiritual.',
      options: [
        {
          label: '"Los perdono por el dolor que me heredaron."',
          value: 'perdono',
        },
        {
          label: '"Gracias por la vida, ahora yo elijo mi propio camino."',
          value: 'gracias',
        },
        {
          label: '"Ayúdenme a cargar con este peso, no puedo solo."',
          value: 'ayuda',
        },
        {
          label: '"Rompo las cadenas, el sufrimiento termina conmigo."',
          value: 'rompo',
        },
      ],
    },
    {
      id: 'fam_5',
      text: '¿Qué es lo que más deseas construir para las futuras generaciones (hijos físicos o espirituales)?',
      justification:
        'El futuro de tu linaje depende de la semilla que plantes hoy tras la lectura.',
      options: [
        {
          label: 'Un entorno de libertad emocional absoluta.',
          value: 'libertad',
        },
        {
          label: 'Seguridad, estabilidad y raíces fuertes.',
          value: 'seguridad',
        },
        { label: 'Sabiduría ancestral libre de traumas.', value: 'sabiduria' },
        {
          label: 'Amor incondicional, sin juicios ni expectativas.',
          value: 'amor',
        },
      ],
    },
  ],
  bienestar: [
    {
      id: 'bien_1',
      text: '¿En qué parte de tu cuerpo físico sientes que se acumula la tensión de tu alma?',
      justification:
        'El cuerpo es el mapa del espíritu. El Oráculo lee tus dolencias como señales kármicas.',
      options: [
        {
          label: 'En el pecho/corazón (angustia, falta de aire).',
          value: 'pecho',
        },
        {
          label: 'En el cuello y hombros (el peso del mundo).',
          value: 'hombros',
        },
        {
          label: 'En el estómago/vientre (emociones no digeridas).',
          value: 'estomago',
        },
        {
          label: 'En la cabeza (pensamientos en bucle, migrañas).',
          value: 'cabeza',
        },
      ],
    },
    {
      id: 'bien_2',
      text: '¿Cómo describirías tu nivel de energía vital al despertar cada mañana?',
      justification:
        'Las fugas energéticas durante el sueño revelan batallas astrales no resueltas.',
      options: [
        {
          label: 'Drenado, como si hubiera luchado toda la noche.',
          value: 'drenado',
        },
        {
          label: 'Pesado, me cuesta arrancar y enfrentar el día.',
          value: 'pesado',
        },
        {
          label: 'Ansioso, mi mente despierta antes que mi cuerpo.',
          value: 'ansioso',
        },
        {
          label: 'Variable, dependo de factores externos para tener fuerza.',
          value: 'variable',
        },
      ],
    },
    {
      id: 'bien_3',
      text: '¿Qué hábito o mecanismo usas para adormecer el dolor cuando se vuelve intenso?',
      justification:
        'Identificar el velo ilusorio permite a la Llave de Constantinopla mostrarte la verdad desnuda.',
      options: [
        { label: 'Aislamiento extremo y silencio.', value: 'aislamiento' },
        { label: 'Consumo (comida, compras, sustancias).', value: 'consumo' },
        {
          label: 'Distracción constante (pantallas, trabajo excesivo).',
          value: 'distraccion',
        },
        {
          label: 'Complacer a otros para no pensar en mí.',
          value: 'complacer',
        },
      ],
    },
    {
      id: 'bien_4',
      text: 'Si tuvieras que definir la "enfermedad" de tu espíritu actual, ¿cuál sería?',
      justification:
        'El autodiagnóstico místico es el primer paso hacia el Elixir Vital.',
      options: [
        { label: 'Desesperanza crónica.', value: 'desesperanza' },
        { label: 'Desconexión de la intuición.', value: 'desconexion' },
        { label: 'Miedo paralizante al cambio.', value: 'miedo' },
        { label: 'Falta de amor propio y auto-castigo.', value: 'castigo' },
      ],
    },
    {
      id: 'bien_5',
      text: '¿Estás dispuesto a mirar tus sombras más oscuras para encontrar la luz de la sanación?',
      justification:
        'La verdadera sanación no es cómoda. El Oráculo requiere tu valentía absoluta.',
      options: [
        {
          label: 'Sí, no tengo miedo de lo que pueda encontrar.',
          value: 'valiente',
        },
        {
          label: 'Me aterra, pero no puedo seguir viviendo así.',
          value: 'aterra',
        },
        {
          label: 'Necesito guía, no sé si soy lo suficientemente fuerte.',
          value: 'guia',
        },
        {
          label: 'Avanzaré poco a poco, respetando mis tiempos.',
          value: 'poco_a_poco',
        },
      ],
    },
  ],
}
